import { useEffect } from 'react';
import { api } from '../lib/api';

/**
 * ChatWidget — injects a live-chat embed on the public site, controlled
 * entirely from the admin panel (Site Settings → Live chat).
 *
 * Reads /api/public/chat-widget. If enabled, injects the right script for
 * the chosen provider:
 *   * tawk   — Tawk.to (widget_id = "<propertyId>/<widgetId>")
 *   * crisp  — Crisp (widget_id = website id)
 *   * custom — raw HTML/script the admin pasted
 *
 * Renders nothing itself. Cleans up its injected nodes on unmount so a
 * config change after a soft navigation doesn't double-inject.
 */
const MARK = 'data-voipinger-chat';

function inject(html) {
  // Parse the snippet and append its scripts so they actually execute
  // (innerHTML alone won't run <script>).
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  const nodes = [];
  tmp.childNodes.forEach((node) => {
    if (node.tagName === 'SCRIPT') {
      const s = document.createElement('script');
      [...node.attributes].forEach((a) => s.setAttribute(a.name, a.value));
      s.textContent = node.textContent;
      s.setAttribute(MARK, '1');
      document.body.appendChild(s);
      nodes.push(s);
    } else {
      node.setAttribute?.(MARK, '1');
      document.body.appendChild(node);
      nodes.push(node);
    }
  });
  return nodes;
}

function tawkSnippet(widgetId) {
  // widgetId format: "propertyId/widgetId"
  const [propertyId, w = 'default'] = (widgetId || '').split('/');
  if (!propertyId) return '';
  return `<script>
    var Tawk_API=Tawk_API||{},Tawk_LoadStart=new Date();
    (function(){
      var s1=document.createElement("script"),s0=document.getElementsByTagName("script")[0];
      s1.async=true;
      s1.src='https://embed.tawk.to/${propertyId}/${w}';
      s1.charset='UTF-8';
      s1.setAttribute('crossorigin','*');
      s0.parentNode.insertBefore(s1,s0);
    })();
  </script>`;
}

function crispSnippet(websiteId) {
  if (!websiteId) return '';
  return `<script>
    window.$crisp=[];window.CRISP_WEBSITE_ID="${websiteId}";
    (function(){var d=document,s=d.createElement("script");
      s.src="https://client.crisp.chat/l.js";s.async=1;
      d.getElementsByTagName("head")[0].appendChild(s);})();
  </script>`;
}

export default function ChatWidget() {
  useEffect(() => {
    let nodes = [];
    let cancelled = false;

    api.get('/api/public/chat-widget')
      .then((res) => {
        if (cancelled) return;
        const c = res.data || {};
        if (!c.enabled) return;
        let html = '';
        if (c.provider === 'tawk') html = tawkSnippet(c.widget_id);
        else if (c.provider === 'crisp') html = crispSnippet(c.widget_id);
        else if (c.provider === 'custom') html = c.custom_html || '';
        if (html) nodes = inject(html);
      })
      .catch(() => { /* widget is optional — fail silent */ });

    return () => {
      cancelled = true;
      document.querySelectorAll(`[${MARK}]`).forEach((n) => n.remove());
      // Best-effort: hide any widget iframe the provider injected itself.
      document.querySelectorAll('iframe[title*="chat" i]').forEach((n) => n.remove());
    };
  }, []);

  return null;
}
